/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React from "react"
import { Row } from "@duxton-web/ui"

import { Image } from "components/Image"
import { useTranslations } from "common/utils/i18n"
import { isUndefined } from "lodash"
import { useIsAllowSaleAlcohol } from "pages/menu/detail/hooks/useIsAllowSaleAlcohol"
import { useIsAllowSaleTobacco } from "pages/menu/detail/hooks/useIsAllowSaleTobacco"
import { useIsBcrsEnabled } from "pages/menu/detail/hooks/useIsBcrsEnabled"
import { AttributeDrawerReviewInfo } from "./AttributeDrawerReviewInfo"
import { AttributeDrawerReviewCarousel } from "./AttributeDrawerReviewCarousel"
import styles from "./AttributeDrawerReview.modules.css"
import { TAttributeDrawerReview } from "./AttributeDrawerReview.model"

function AttributeDrawerReview({
  imagesSrcList,
  price,
  menuName,
  dataSource,
  isAlcohol,
  isTobacco,
  isBcrsEligible,
  bcrsUnitCount,
}: TAttributeDrawerReview) {
  const [
    yesText,
    noText,
    itemContainsAlcoholText,
    itemContainsTobaccoText,
    bcrsEligibilityLabel,
    bcrsContainerLabel,
  ] = useTranslations([
    "common.yes",
    "common.no",
    "alcoholSetting.itemContainsAlcohol",
    "tobaccoSetting.itemContainsTobacco",
    "menu.bcrs.item.eligibilityLabel",
    "menu.bcrs.item.unitCountReadonlyLabel",
  ])

  const isAllowSaleAlcohol = useIsAllowSaleAlcohol()
  const isAllowSaleTobacco = useIsAllowSaleTobacco()
  const isBcrsEnabled = useIsBcrsEnabled()

  return (
    // TODO: add translation
    <div>
      <header className={styles.header}>
        {imagesSrcList.length > 0 ? (
          <AttributeDrawerReviewCarousel imageSrcList={imagesSrcList} />
        ) : (
          <div className={styles.emptyImageWrapper} data-testid="empty-image">
            <Image src="" height={276} width={276} />
          </div>
        )}
        <div>
          <Row>
            <span className={styles.labelPrice}>{price}</span>
          </Row>
          <Row>
            <span className={styles.labelMenuName}>{menuName}</span>
          </Row>
        </div>
      </header>

      <div className={styles.body}>
        {!isUndefined(isAlcohol) && isAllowSaleAlcohol && (
          <AttributeDrawerReviewInfo
            label={itemContainsAlcoholText}
            value={isAlcohol ? yesText : noText}
          />
        )}

        {!isUndefined(isTobacco) && isAllowSaleTobacco && (
          <AttributeDrawerReviewInfo
            label={itemContainsTobaccoText}
            value={isTobacco ? yesText : noText}
          />
        )}

        {dataSource.map(v => (
          <AttributeDrawerReviewInfo
            key={v.label}
            label={v.label}
            value={v.value}
            labelIcon={v.icon}
          />
        ))}

        {isBcrsEnabled && (
          <React.Fragment>
            <AttributeDrawerReviewInfo
              label={bcrsEligibilityLabel}
              value={isBcrsEligible ? yesText : noText}
            />
            {isBcrsEligible && !isUndefined(bcrsUnitCount) && (
              <AttributeDrawerReviewInfo
                label={bcrsContainerLabel}
                value={String(bcrsUnitCount)}
              />
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  )
}

export default AttributeDrawerReview
